import { useState, useEffect } from 'react';
import Head from 'next/head';
import { renderMetaTags, useQuerySubscription } from 'react-datocms';
import FlipMove from 'react-flip-move';
import useSWRInfinite from 'swr/infinite';
import cn from 'classnames';

import { request, hasContent } from 'lib/datocms';
import { encode, parseSlug } from 'lib/utils';
import { SEARCH_COPY, PAGE_SIZE } from 'lib/constants';

import {
  Alert,
  Container,
  PageIntro,
  Layout,
  GeneralPageBody,
  PageTile,
  MultiSelectFilter,
  PageHero,
  PageCTA,
  Loader,
} from 'components';

import {
  metaTagsFragment,
  contentBlocks,
  pageHeroImageFragment,
  contentFragments,
  responsiveImageFragment,
  personTileProps,
  menuFragment,
  pageCTAFragment,
} from 'lib/fragments';

export async function getStaticProps({ params, preview = false }) {
  const graphqlRequest = {
    query: `
      query PeopleLandingPage {
        menu {
          ...menuFragment
        }
        peopleLandingPage {
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          title
          introduction(markdown: true)
          ${pageHeroImageFragment}
          slug
          content {
            ${contentBlocks}
          }
          callToAction {
            ...pageCTAFragment
          }
        }
        allTeamMembers(
          first: ${PAGE_SIZE}
          orderBy: [name_ASC]
        ) {
          ${personTileProps}
        }
        locations: allLocations(orderBy: name_ASC) {
          label: name
          value: id
        }
        expertises: allExpertises(orderBy: name_ASC) {
          label: name
          value: id
        }
      }

      ${metaTagsFragment}
      ${contentFragments}
      ${responsiveImageFragment}
      ${menuFragment}
      ${pageCTAFragment}
    `,
    preview,
  };
  return {
    props: {
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData: await request(graphqlRequest),
            token: process.env.NEXT_DATOCMS_API_TOKEN,
          }
        : {
            enabled: false,
            initialData: await request(graphqlRequest),
          },
    },
    revalidate: 120,
  };
}

const fetcher = (url) =>
  fetch(url).then((res) => {
    return res.json();
  });

export default function PeopleLandingPage({ subscription }) {
  const {
    data: { peopleLandingPage, allTeamMembers, locations, expertises, menu },
  } = useQuerySubscription(subscription);

  const [filterLocations, setFilterLocations] = useState([]);
  const [filterExpertises, setFilterExpertises] = useState([]);
  const [results, setResults] = useState(allTeamMembers);
  const [search, setSearch] = useState('');

  const metaTags = peopleLandingPage.seo;
  const heroImage = peopleLandingPage.image;
  const callToAction = peopleLandingPage.callToAction;

  const { data, error, size, setSize } = useSWRInfinite((index) => {
    return `/api/people?${encode({
      first: PAGE_SIZE,
      skip: index * PAGE_SIZE,
      locations: filterLocations,
      expertises: filterExpertises,
      search: search.length > 2 ? search : '',
    })}`;
  }, fetcher);

  useEffect(() => {
    if (data !== undefined) {
      let newResults = [];
      data?.map((values) => (newResults = newResults.concat(values.allTeamMembers)));
      setResults(newResults);
    }
  }, [data]);

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = results.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.allTeamMembers?.length < PAGE_SIZE);

  return (
    <Layout preview={subscription.preview} menu={menu}>
      {metaTags && <Head>{renderMetaTags(metaTags)}</Head>}

      {heroImage && <PageHero responsiveImage={heroImage.responsiveImage} />}

      <PageIntro title={peopleLandingPage.title} introduction={peopleLandingPage.introduction} />

      <section className="PageBody PageBody--people">
        {hasContent(peopleLandingPage) && <GeneralPageBody content={peopleLandingPage.content} />}

        <Container>
          <div className="Grid u-mB-md">
            <div className="Grid-cell u-md-width1of3 u-sm-mB-sm">
              <label htmlFor="search" className="Input Input--rounded-heavy Input--grey u-pT-0">
                <h4>Name</h4>
                <div>
                  <input
                    id="search"
                    type="search"
                    placeholder="Search..."
                    aria-label="Search the Team Members"
                    spellCheck="true"
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                  />
                  {isLoadingMore && (
                    <span className="Input-spinner">
                      <Loader />
                    </span>
                  )}
                </div>
              </label>
            </div>
            <div className="Grid-cell u-md-width1of3 u-sm-mB-sm">
              <MultiSelectFilter
                label="Location"
                options={locations}
                onChange={(value) => {
                  setFilterLocations(value.map((v) => v.value));
                }}
                isLoading={isLoadingMore}
              />
            </div>
            <div className="Grid-cell u-md-width1of3">
              <MultiSelectFilter
                label="Expertise"
                options={expertises}
                onChange={(value) => {
                  setFilterExpertises(value.map((v) => v.value));
                }}
                isLoading={isLoadingMore}
              />
            </div>
          </div>

          <FlipMove
            staggerDelayBy={20}
            staggerDurationBy={15}
            className="Grid u-flex u-flexWrap u-flexAlignItemsStretch"
          >
            {isEmpty && <Alert type="info" className="u-width1of1" message={SEARCH_COPY.NO_RESULTS} />}
            {!!results &&
              results.map((person) => (
                <div
                  className="Grid-cell u-width1of2 u-md-width1of3 u-xl-width1of4 u-mB-sm u-lg-mB-md"
                  key={`page-tile-${person.id}`}
                >
                  <PageTile
                    title={person.name || ''}
                    image={person.image}
                    url={parseSlug(person)}
                    introduction={person.jobTitle}
                    portrait
                  />
                </div>
              ))}
          </FlipMove>
          {!isReachingEnd && (
            <div className="u-width1of1 u-flex u-flexRow u-justifyCenter u-alignCenter">
              <button
                className={cn('Button Button--tertiary Button--large Button--rounded-heavy', {
                  'is-disabled': isLoadingMore || isReachingEnd,
                })}
                disabled={isLoadingMore || isReachingEnd}
                onClick={() => setSize(size + 1)}
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </Container>
      </section>

      {!!callToAction && (
        <PageCTA
          title={callToAction.title}
          caption={callToAction.caption}
          btnLabel={callToAction.buttonLabel}
          btnURL={callToAction.buttonUrl}
        />
      )}
    </Layout>
  );
}
